import { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { money } from "../lib/constants";
import { toast } from "sonner";
import { Printer, FileText, Presentation, Camera, Clock, X, GitCompare, Download, Trash2 } from "lucide-react";

const DEFAULT_ORDER = [{ key: "narrative", enabled: true }, { key: "health", enabled: true }, { key: "budget", enabled: true }, { key: "workstreams", enabled: true }];
const STATUSES = ["On Track", "At Risk", "Critical", "Not Started"];

export default function Reports() {
  const { effectiveRole } = useAuth();
  const canSnapshot = ["pmo", "it_admin"].includes(effectiveRole);
  const [r, setR] = useState(null);
  const [snaps, setSnaps] = useState([]);
  const [viewSnap, setViewSnap] = useState(null);
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");
  const loadSnaps = () => api.get("/report/snapshots").then((res) => setSnaps(res.data)).catch(() => {});
  useEffect(() => { api.get("/report").then((res) => setR(res.data)).catch(() => {}); loadSnaps(); }, []);
  const saveSnapshot = async () => {
    try { await api.post("/report/snapshot"); toast.success("Snapshot archived"); loadSnaps(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const deleteSnap = async (id) => {
    if (!window.confirm("Delete this snapshot permanently?")) return;
    try { await api.delete(`/report/snapshots/${id}`); toast.success("Snapshot deleted"); loadSnaps(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const downloadPdf = async (snap) => {
    try {
      const res = await api.get(`/report/snapshots/${snap.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `carle_snapshot_${new Date(snap.generated_at).toISOString().slice(0, 10)}.pdf`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const downloadComparePdf = async () => {
    if (!cmpA || !cmpB || cmpA === cmpB) return;
    try {
      const res = await api.get(`/report/compare/pdf?a=${cmpA}&b=${cmpB}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const el = document.createElement("a"); el.href = url; el.download = "carle_snapshot_comparison.pdf"; el.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };
  if (!r) return <div className="text-[#5A687C]">Loading report…</div>;

  const total = Object.values(r.rollup).reduce((a, b) => a + b, 0);

  const snapSection = (key) => {
    if (key === "narrative") return viewSnap.narrative ? <p key="narrative" className="text-sm text-[#1F2733] leading-relaxed whitespace-pre-wrap bg-[#F4F6FA] rounded-2xl p-4">{viewSnap.narrative}</p> : null;
    if (key === "health") return (
      <div key="health"><h3 className="font-bold text-[#1F2733] mb-3">Program health</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(viewSnap.rollup || {}).map(([s, c]) => (<div key={s} className="rounded-2xl border border-[#DCE2EA] p-3"><div className="text-2xl font-extrabold text-[#1F2733]">{c}</div><div className="mt-1"><StatusBadge status={s} /></div></div>))}
        </div></div>);
    if (key === "budget") return (
      <div key="budget"><h3 className="font-bold text-[#1F2733] mb-3">Budget</h3>
        <div className="flex gap-8 text-sm"><div><div className="text-xl font-extrabold text-[#BA0C2F]">{money(viewSnap.budget?.planned || 0)}</div><div className="text-[#5A687C]">Planned</div></div><div><div className="text-xl font-extrabold text-[#BA0C2F]">{money(viewSnap.budget?.actual || 0)}</div><div className="text-[#5A687C]">Actual</div></div></div></div>);
    if (key === "workstreams") return (
      <div key="workstreams"><h3 className="font-bold text-[#1F2733] mb-3">Workstream status</h3>
        <table className="w-full text-sm"><tbody>{(viewSnap.workstreams || []).map((w, i) => (<tr key={i} className="border-b border-[#DCE2EA]/60"><td className="py-2 font-bold text-[#5A687C] w-16">{w.code}</td><td className="py-2">{w.title}</td><td className="py-2 text-right"><StatusBadge status={w.status} /></td></tr>))}</tbody></table></div>);
    return null;
  };

  const liveSnap = { id: "live", generated_at: new Date().toISOString(), generated_by: "Current (live)", rollup: r.rollup, budget: r.budget, workstreams: (r.workstreams || []).map((w) => ({ code: w.code, title: w.title, status: w.status || w.derived_status })) };
  const cmpPool = [liveSnap, ...snaps];
  const A = cmpPool.find((s) => s.id === cmpA);
  const B = cmpPool.find((s) => s.id === cmpB);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1F2733]">Executive Report</h1>
          <p className="text-[#5A687C] mt-1">Assembled live from published program data.</p>
        </div>
        <div className="flex flex-wrap gap-2 print:hidden">
          <button data-testid="export-pdf-btn" onClick={async () => { try { const res = await api.get("/report/export/pdf", { responseType: "blob" }); const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = "carle_executive_report.pdf"; a.click(); toast.success("PDF downloaded"); } catch (e) { toast.error(apiErr(e)); } }}
            className="inline-flex items-center gap-1.5 bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full px-5 py-2.5 font-semibold">
            <FileText className="h-4 w-4" /> Export PDF
          </button>
          <button data-testid="export-pptx-btn" onClick={async () => { try { const res = await api.get("/report/export/pptx", { responseType: "blob" }); const u = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = u; a.download = "carle_executive_report.pptx"; a.click(); toast.success("PowerPoint downloaded"); } catch (e) { toast.error(apiErr(e)); } }}
            className="inline-flex items-center gap-1.5 bg-[#BA0C2F] hover:opacity-90 text-white rounded-full px-5 py-2.5 font-semibold">
            <Presentation className="h-4 w-4" /> Export PowerPoint
          </button>
          <button data-testid="print-report-btn" onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 bg-[#F4F6FA] hover:bg-[#DCE2EA] border border-[#DCE2EA] rounded-full px-5 py-2.5 font-semibold text-[#1F2733]">
            <Printer className="h-4 w-4" /> Print
          </button>
          {canSnapshot && (
            <button data-testid="save-snapshot-btn" onClick={saveSnapshot}
              className="inline-flex items-center gap-1.5 bg-[#1F2733] hover:bg-[#0f141b] text-white rounded-full px-5 py-2.5 font-semibold">
              <Camera className="h-4 w-4" /> Save Snapshot
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 lg:p-10 space-y-8">
        <div className="border-b border-[#DCE2EA] pb-6">
          <div className="text-xs font-bold text-[#BA0C2F] uppercase tracking-wider">Carle Health IT · Digital Transformation</div>
          <h2 className="text-2xl font-extrabold text-[#1F2733] mt-1">Executive Program Status Report</h2>
          <div className="text-sm text-[#5A687C] mt-1">Generated {new Date(r.generated_at).toLocaleString()}</div>
        </div>

        {r.narrative && (
          <div>
            <h3 className="font-bold text-[#1F2733] flex items-center gap-2 mb-2"><FileText className="h-5 w-5 text-[#BA0C2F]" /> PMO Commentary</h3>
            <p className="text-sm text-[#1F2733] leading-relaxed whitespace-pre-wrap bg-[#F4F6FA] rounded-2xl p-4">{r.narrative}</p>
          </div>
        )}

        <div>
          <h3 className="font-bold text-[#1F2733] mb-3">Program health</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(r.rollup).map(([s, c]) => (
              <div key={s} className="rounded-2xl border border-[#DCE2EA] p-4">
                <div className="text-3xl font-extrabold text-[#1F2733]">{c}</div>
                <div className="mt-1"><StatusBadge status={s} /></div>
                <div className="text-xs text-[#98A2AF] mt-1">{total ? Math.round((c / total) * 100) : 0}%</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-bold text-[#1F2733] mb-3">Budget summary</h3>
          <div className="flex gap-8 text-sm">
            <div><div className="text-2xl font-extrabold text-[#BA0C2F]">{money(r.budget.planned)}</div><div className="text-[#5A687C]">Planned</div></div>
            <div><div className="text-2xl font-extrabold text-[#BA0C2F]">{money(r.budget.actual)}</div><div className="text-[#5A687C]">Actual</div></div>
            <div><div className="text-2xl font-extrabold text-[#2E8B57]">{money(r.budget.planned - r.budget.actual)}</div><div className="text-[#5A687C]">Remaining</div></div>
          </div>
        </div>

        <div>
          <h3 className="font-bold text-[#1F2733] mb-3">Workstream status</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-[#5A687C] text-xs font-bold uppercase border-b border-[#DCE2EA]"><th className="text-left py-2">Code</th><th className="text-left py-2">Workstream</th><th className="text-left py-2">Phase</th><th className="text-left py-2">Status</th></tr></thead>
            <tbody>
              {r.workstreams.map((w) => (
                <tr key={w.id} className="border-b border-[#DCE2EA]/60"><td className="py-2 font-bold text-[#5A687C]">{w.code}</td><td className="py-2">{w.title}</td><td className="py-2 text-[#5A687C]">{w.phase}</td><td className="py-2"><StatusBadge status={w.derived_status || w.status} /></td></tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          <h3 className="font-bold text-[#1F2733] mb-3">Upcoming milestones</h3>
          <div className="space-y-1.5">
            {r.milestones.map((m) => (
              <div key={m.id} className="flex items-center gap-3 text-sm"><span className="font-semibold text-[#BA0C2F] w-28">{new Date(m.date).toLocaleDateString()}</span><span className="flex-1">{m.title}</span><StatusBadge status={m.status} /></div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 print:hidden" data-testid="snapshot-history">
        <h3 className="font-bold text-[#1F2733] flex items-center gap-2 mb-1"><Clock className="h-5 w-5 text-[#BA0C2F]" /> Report snapshots</h3>
        <p className="text-xs text-[#5A687C] mb-4">Permanent, timestamped archives of the executive report. Click one to view it exactly as it was captured.</p>
        <div className="space-y-2">
          {snaps.length === 0 && <div className="text-sm text-[#5A687C]">No snapshots archived yet.</div>}
          {snaps.map((s) => {
            const total = Object.values(s.rollup || {}).reduce((a, b) => a + b, 0);
            return (
              <div key={s.id} className="flex items-center gap-2">
                <button data-testid={`snapshot-${s.id}`} onClick={() => setViewSnap(s)}
                  className="flex-1 flex items-center gap-3 p-3 rounded-xl border border-[#DCE2EA] hover:border-[#BA0C2F] hover:bg-[#FBEAED]/40 text-left transition-all">
                  <Camera className="h-4 w-4 text-[#BA0C2F] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#1F2733]">{new Date(s.generated_at).toLocaleString()}</div>
                    <div className="text-[11px] text-[#98A2AF]">by {s.generated_by} · {(s.workstreams || []).length} workstreams · planned {money(s.budget?.planned || 0)}</div>
                  </div>
                  <span className="text-[11px] text-[#98A2AF]">{total} WS</span>
                </button>
                {canSnapshot && <button data-testid={`delete-snapshot-${s.id}`} onClick={() => deleteSnap(s.id)} className="text-[#98A2AF] hover:text-[#BA0C2F] p-2 shrink-0" title="Delete snapshot"><Trash2 className="h-4 w-4" /></button>}
              </div>
            );
          })}
        </div>
      </div>

      {snaps.length >= 1 && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6 print:hidden" data-testid="snapshot-compare">
          <div className="flex items-start justify-between gap-3 mb-1">
            <h3 className="font-bold text-[#1F2733] flex items-center gap-2"><GitCompare className="h-5 w-5 text-[#BA0C2F]" /> Compare snapshots</h3>
            <button data-testid="download-compare-pdf" onClick={downloadComparePdf} disabled={!(A && B && A.id !== B.id)}
              className="inline-flex items-center gap-1.5 bg-[#BA0C2F] hover:bg-[#8A0822] disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-full px-4 py-2 text-sm font-semibold shrink-0"><Download className="h-4 w-4" /> Comparison PDF</button>
          </div>
          <p className="text-xs text-[#5A687C] mb-4">Pick two archived snapshots to see how status and budget shifted between them.</p>
          <div className="grid sm:grid-cols-2 gap-3 mb-5">
            <select data-testid="compare-a" value={cmpA} onChange={(e) => setCmpA(e.target.value)} className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm bg-white">
              <option value="">Snapshot A…</option>
              {cmpPool.map((s) => <option key={s.id} value={s.id}>{s.id === "live" ? "Current program (live)" : new Date(s.generated_at).toLocaleString()}</option>)}
            </select>
            <select data-testid="compare-b" value={cmpB} onChange={(e) => setCmpB(e.target.value)} className="px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm bg-white">
              <option value="">Snapshot B…</option>
              {cmpPool.map((s) => <option key={s.id} value={s.id}>{s.id === "live" ? "Current program (live)" : new Date(s.generated_at).toLocaleString()}</option>)}
            </select>
          </div>
          {A && B && A.id !== B.id ? (
            <div className="space-y-5" data-testid="compare-result">
              <div className="grid grid-cols-2 gap-4 text-xs text-[#5A687C]"><div>A · {new Date(A.generated_at).toLocaleString()}</div><div>B · {new Date(B.generated_at).toLocaleString()}</div></div>
              <div>
                <h4 className="text-xs font-bold uppercase text-[#5A687C] mb-2">Status counts</h4>
                <div className="space-y-1.5">
                  {STATUSES.map((s) => {
                    const av = (A.rollup || {})[s] || 0, bv = (B.rollup || {})[s] || 0, d = bv - av;
                    return (<div key={s} className="flex items-center gap-3 text-sm"><span className="w-28"><StatusBadge status={s} /></span><span className="text-[#5A687C] w-10 text-right">{av}</span><span className="text-[#98A2AF]">→</span><span className="text-[#1F2733] font-semibold w-10">{bv}</span>{d !== 0 && <span className={`text-xs font-bold ${d > 0 ? "text-[#BA0C2F]" : "text-[#2E8B57]"}`}>{d > 0 ? `+${d}` : d}</span>}</div>);
                  })}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase text-[#5A687C] mb-2">Budget</h4>
                {[["Planned", "planned"], ["Actual", "actual"]].map(([label, k]) => {
                  const av = (A.budget || {})[k] || 0, bv = (B.budget || {})[k] || 0, d = bv - av;
                  return (<div key={k} className="flex items-center gap-3 text-sm"><span className="w-20 text-[#5A687C]">{label}</span><span className="w-24 text-right">{money(av)}</span><span className="text-[#98A2AF]">→</span><span className="w-24 font-semibold">{money(bv)}</span>{d !== 0 && <span className={`text-xs font-bold ${d > 0 ? "text-[#BA0C2F]" : "text-[#2E8B57]"}`}>{d > 0 ? "+" : ""}{money(d)}</span>}</div>);
                })}
              </div>
              <div>
                <h4 className="text-xs font-bold uppercase text-[#5A687C] mb-2">Workstream status changes</h4>
                {(() => {
                  const bmap = {}; (B.workstreams || []).forEach((w) => { bmap[w.code] = w; });
                  const changed = (A.workstreams || []).filter((w) => bmap[w.code] && bmap[w.code].status !== w.status);
                  if (changed.length === 0) return <div className="text-xs text-[#98A2AF]">No workstream status changes.</div>;
                  return <div className="space-y-1.5">{changed.map((w) => (<div key={w.code} className="flex items-center gap-3 text-sm"><span className="font-bold text-[#5A687C] w-16">{w.code}</span><StatusBadge status={w.status} /><span className="text-[#98A2AF]">→</span><StatusBadge status={bmap[w.code].status} /></div>))}</div>;
                })()}
              </div>
            </div>
          ) : <div className="text-xs text-[#98A2AF]">Select two different snapshots to compare.</div>}
        </div>
      )}

      {viewSnap && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="snapshot-viewer">
          <div className="bg-white rounded-[18px] max-w-3xl w-full my-8 p-6 lg:p-10 space-y-6">
            <div className="flex items-start justify-between gap-3 border-b border-[#DCE2EA] pb-4">
              <div>
                <div className="text-xs font-bold text-[#BA0C2F] uppercase tracking-wider">Archived Snapshot</div>
                <h2 className="text-xl font-extrabold text-[#1F2733] mt-1">Executive Report</h2>
                <div className="text-sm text-[#5A687C] mt-1">Captured {new Date(viewSnap.generated_at).toLocaleString()} by {viewSnap.generated_by}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid="download-snapshot-pdf" onClick={() => downloadPdf(viewSnap)} className="inline-flex items-center gap-1.5 bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full px-4 py-2 text-sm font-semibold"><Download className="h-4 w-4" /> Download PDF</button>
                <button data-testid="close-snapshot-btn" onClick={() => setViewSnap(null)} className="text-[#98A2AF] hover:text-[#BA0C2F]"><X className="h-5 w-5" /></button>
              </div>
            </div>
            {(viewSnap.sections && viewSnap.sections.length ? viewSnap.sections : DEFAULT_ORDER).filter((s) => s.enabled).map((s) => snapSection(s.key))}
          </div>
        </div>
      )}
    </div>
  );
}
